import { v4 as uuidv4 } from 'uuid';

export type ChangeListener<K, V> = (
  key: K,
  value: V | undefined,
  oldValue: V | undefined
) => void;

type Uuid = string;

/**
 * Implementation of Map with a change listening logic.
 */
export class ObservableMap<K, V> {
  private map: Map<K, V>;
  private changeListeners: Map<Uuid, ChangeListener<K, V>> = new Map<
    Uuid,
    ChangeListener<K, V>
  >();

  constructor(entries?: ReadonlyArray<[K, V]>) {
    this.map = new Map<K, V>(entries);
  }

  get(key: K): V | undefined {
    return this.map.get(key);
  }

  entries(): IterableIterator<[K, V]> {
    return this.map.entries();
  }

  set(key: K, value: V): void {
    const oldValue = this.map.get(key);
    this.map.set(key, value);
    this.notifyListeners(key, value, oldValue);
  }

  delete(key: K): boolean {
    const hadKey = this.map.has(key);
    const oldValue = this.map.get(key);
    this.map.delete(key);
    if (hadKey) {
      this.notifyListeners(key, undefined, oldValue);
    }
    return hadKey;
  }

  /**
   * Cache invalidation uses per-key notifications so pending waiters settle
   * consistently when upstream inputs change.
   */
  clear(): void {
    const keys = Array.from(this.map.keys());
    for (const key of keys) {
      this.delete(key);
    }
  }

  private notifyListeners(
    key: K,
    value: V | undefined,
    oldValue: V | undefined
  ): void {
    this.changeListeners.forEach(listener => {
      listener(key, value, oldValue);
    });
  }

  addChangeListener(listener: ChangeListener<K, V>): Uuid {
    const uuid = uuidv4();
    this.changeListeners.set(uuid, listener);
    return uuid;
  }

  removeChangeListener(uuid: Uuid): void {
    if (this.changeListeners.has(uuid)) {
      this.changeListeners.delete(uuid);
    }
  }

  /**
   * Returns a Promise that will resolve with the value of the provided key when it changes, and the value condition is met.
   * If the change is not received within the specified timeout, it will return defaultValue (undefined by default).
   */
  waitForValue(
    key: K,
    valueCondition: (val: V | undefined) => boolean,
    timeoutMs: number,
    defaultValue?: V
  ): Promise<V | undefined> {
    return new Promise<V | undefined>((resolve, reject) => {
      let listenerUuid: Uuid;

      // Removes the listener
      const cleanListener = () => {
        if (listenerUuid) {
          this.removeChangeListener(listenerUuid);
        }
      };

      try {
        // Resolve undefined when the timeout ends
        setTimeout(() => {
          cleanListener();
          resolve(defaultValue);
        }, timeoutMs);

        // Listen for value changes and return it when it matches the key and it's not undefined
        listenerUuid = this.addChangeListener((k, v) => {
          if (k === key && valueCondition(v)) {
            cleanListener();
            resolve(v);
          }
        });
      } catch (e) {
        cleanListener();
        reject(e);
      }
    });
  }
}
